import React, { useMemo } from 'react'
import CmdKGroup from 'src/components/Cmdk/CmdKGroup'
import CommandItem from 'src/components/Cmdk/CmdKItem'
import { filter } from 'src/components/Cmdk/filter'
import type { CommandMenuGroup } from 'src/components/Cmdk/Cmdk'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'

export interface CmdkSubMenuItem {
  label: string
  startIcon?: React.ReactNode
  subtext?: string
  disabled?: boolean
  keywords?: string[]
  onClick?: () => void
  onSelect?: () => void
  closeOnSelect?: boolean
}

export interface CmdkSubMenuProps {
  heading: string
  parentLabel: string
  parentSubtext?: string
  parentStartIcon?: React.ReactNode
  items: CmdkSubMenuItem[]
  search: string
  onNavigate: (groups: CommandMenuGroup[]) => void
  pages: CommandMenuGroup[][]
  onClose?: () => void
  onBack?: () => void
}

const CmdkSubMenu: React.FC<CmdkSubMenuProps> = ({
  heading,
  parentLabel,
  parentSubtext,
  parentStartIcon,
  items,
  search,
  onNavigate,
  pages,
  onBack,
}) => {
  const topLevel = pages.length === 0

  const groupedItems = useMemo(() => {
    const childItems = items.map((item) => ({
      label: item.label,
      startIcon: item.startIcon,
      subtext: item.subtext,
      disabled: item.disabled,
      onClick: item.onClick ?? item.onSelect,
      closeOnSelect: item.onClick ? item.closeOnSelect : true,
    }))
    if (onBack) {
      return [
        {
          label: 'Back',
          startIcon: <ArrowBackIosNewRoundedIcon />,
          subtext: '',
          onClick: onBack,
          closeOnSelect: false,
        },
        ...childItems,
      ]
    }
    return childItems
  }, [items])

  const filteredChildItems = useMemo(() => {
    return items.filter((item) =>
      filter(item.label, search, item.keywords || [])
    )
  }, [items, search])

  // When navigating deeper, hide root-level submenu entries
  if (!topLevel) return null

  // If searching, surface matching child items directly
  if (search) {
    if (filteredChildItems.length === 0) return null

    return (
      <CmdKGroup heading={heading}>
        {filteredChildItems.map((item) => (
          <CommandItem
            key={item.label}
            startIcon={item.startIcon}
            subtext={item.subtext}
            disabled={item.disabled}
            keywords={item.keywords}
            onSelect={item.onClick ?? item.onSelect}
          >
            {item.label}
          </CommandItem>
        ))}
      </CmdKGroup>
    )
  }

  // Otherwise, show a parent entry that navigates to a submenu page
  const handleParentSelect = () => {
    onNavigate([
      {
        heading,
        items: groupedItems,
      },
    ])
  }

  return (
    <CmdKGroup heading={heading}>
      <CommandItem
        onSelect={handleParentSelect}
        startIcon={parentStartIcon}
        endIcon={<ArrowForwardIosRoundedIcon />}
        subtext={parentSubtext}
      >
        {parentLabel}
      </CommandItem>
    </CmdKGroup>
  )
}

export default CmdkSubMenu
